import type { ClientReviewStatusV1 } from "@enrein/shared-contracts/approval-change";
import type { StudioDepartureKitStatusV1, StudioIntakeSummaryV1, StudioProjectLifecycleViewV1 } from "@enrein/shared-contracts";
import type { StudioMissionControlSummaryV1 } from "@enrein/shared-contracts/studio-dashboard";

export {
  SIMPLE_PROJECT_PHASES,
  STALE_ACTIVE_GENERATION_MS,
  deriveSimpleProjectFacts,
  deriveSimpleProjectPhase,
  deriveStudioProjectLifecycle,
  deriveLifecyclePollHints,
  humanDepartureKitIssuanceLabel,
  isActiveGenerationRunStatus,
  isAwaitingHandoffPackaging,
  isGeneratingRun,
  isHandoffReadyDepartureKit,
  isPackagingDepartureKit,
  isQaFailureRunStatus,
  isStaleActiveGenerationRun,
  latestRun,
  resolveExportRunId,
  resolveStudioHasPreview,
  type SimpleProjectFacts,
  type SimpleProjectPhaseId,
  type SimpleProjectPhaseState,
  type StudioProjectLifecycleDeriveInput,
} from "@enrein/shared-contracts/studio/derive-studio-project-lifecycle";

import {
  isActiveGenerationRunStatus,
  isHandoffReadyDepartureKit,
  isPackagingDepartureKit,
  isQaFailureRunStatus,
  SIMPLE_PROJECT_PHASES,
  type SimpleProjectFacts,
  type SimpleProjectPhaseId,
  type SimpleProjectPhaseState,
} from "@enrein/shared-contracts/studio/derive-studio-project-lifecycle";

function normalizePhaseStatus(
  partial: Partial<Record<SimpleProjectPhaseId, "done" | "current" | "upcoming">>,
): Record<SimpleProjectPhaseId, "done" | "current" | "upcoming"> {
  return Object.fromEntries(
    SIMPLE_PROJECT_PHASES.map((step) => [step.id, partial[step.id] ?? "upcoming"]),
  ) as Record<SimpleProjectPhaseId, "done" | "current" | "upcoming">;
}

export function mapLifecycleViewToPhaseState(
  lifecycle: StudioProjectLifecycleViewV1,
  facts: SimpleProjectFacts | null,
): SimpleProjectPhaseState & { readonly facts: SimpleProjectFacts | null } {
  const staleApproval =
    lifecycle.flags.hasApproval &&
    lifecycle.clientReview != null &&
    !lifecycle.clientReview.approvalMatchesLatestPassingArtifact;
  return {
    phase: lifecycle.simplePhase,
    phaseLabel: lifecycle.phaseLabel,
    phaseStatus: normalizePhaseStatus(lifecycle.phaseStatus),
    primaryCta: lifecycle.primaryCta,
    primaryActionId: lifecycle.primaryActionId,
    nowTitle: lifecycle.nowTitle,
    nowCopy: lifecycle.nowCopy,
    showFactsPanel: lifecycle.simplePhase === "review_and_build",
    showPreviewPanel: lifecycle.flags.hasPreview && !lifecycle.flags.generating,
    showSecondaryPortal: !["preview_live", "handoff_ready"].includes(lifecycle.simplePhase),
    showHandoffPanel:
      lifecycle.flags.hasApproval || lifecycle.flags.handoffReady || lifecycle.flags.packaging,
    showStaleApprovalBanner: staleApproval,
    staleApprovalSummary: staleApproval
      ? (lifecycle.departureKit?.passingVsApproved?.clientSafeSummary ??
        "Client approved an older preview — share the latest preview before packaging handoff.")
      : null,
    facts: lifecycle.simplePhase === "review_and_build" ? facts : null,
    primaryDisabled: lifecycle.primaryDisabled,
    exportRunId: lifecycle.exportRunId,
    qaFailure: lifecycle.flags.qaFailure,
  };
}

export interface GuidedNextActionCard {
  readonly projectId: string;
  readonly clientName: string;
  readonly title: string;
  readonly why: string;
  readonly cta: string;
  readonly actionId: string;
}

export interface GuidedPortfolioProjectInput {
  readonly projectId: string;
  readonly businessName: string | null;
  readonly lastKnownRunStatus?: string | null;
}

export interface CommandCenterStatCard {
  readonly id: string;
  readonly label: string;
  readonly count: number;
  readonly hint: string;
  readonly navigateTo: "projects" | "scan-opportunities" | "project" | null;
}

/** Portfolio-level next-action priority (lower = more urgent). Shared with mission-control derivation. */
export function portfolioNextActionPriority(input: {
  readonly handoffReady: boolean;
  readonly packaging: boolean;
  readonly approved: boolean;
  readonly qaFailure: boolean;
  readonly hasPreview: boolean;
  readonly generating: boolean;
  readonly awaitingClient: boolean;
}): number {
  if (input.handoffReady) return 1;
  if (input.packaging) return 2;
  if (input.approved) return 3;
  if (input.qaFailure) return 4;
  if (input.hasPreview) return 5;
  if (input.generating) return 6;
  if (input.awaitingClient) return 7;
  return 8;
}

function buildPortfolioNextActionCard(input: {
  readonly project: GuidedPortfolioProjectInput;
  readonly review: ClientReviewStatusV1 | null | undefined;
  readonly departureKit: StudioDepartureKitStatusV1 | null | undefined;
}): { readonly card: GuidedNextActionCard; readonly priority: number } {
  const { project, review, departureKit } = input;
  const clientName = project.businessName ?? "Client";
  const hasPreview = Boolean(review?.latestPreview);
  const approved = Boolean(review?.latestApproval);
  const handoffReady = isHandoffReadyDepartureKit(departureKit);
  const packaging =
    approved &&
    !handoffReady &&
    isPackagingDepartureKit(departureKit, project.lastKnownRunStatus ?? undefined);
  const qaFailure = isQaFailureRunStatus(project.lastKnownRunStatus);
  const generating = isActiveGenerationRunStatus(project.lastKnownRunStatus);
  const awaitingClient = Boolean(review?.clientLink?.status?.startsWith("INTAKE"));

  const priority = portfolioNextActionPriority({
    handoffReady,
    packaging,
    approved,
    qaFailure,
    hasPreview,
    generating,
    awaitingClient,
  });

  if (handoffReady) {
    return {
      priority,
      card: {
        projectId: project.projectId,
        clientName,
        title: "Download handoff bundle",
        why: "Departure Kit is ready for delivery.",
        cta: "Open project",
        actionId: "open-project",
      },
    };
  }
  if (packaging) {
    return {
      priority,
      card: {
        projectId: project.projectId,
        clientName,
        title: "Packaging in progress",
        why: "Export and Departure Kit composition are running.",
        cta: "Open project",
        actionId: "open-project",
      },
    };
  }
  if (approved) {
    return {
      priority,
      card: {
        projectId: project.projectId,
        clientName,
        title: `Package handoff for ${clientName}`,
        why: "Client approved — export the Departure Kit next.",
        cta: "Open project",
        actionId: "open-project",
      },
    };
  }
  if (qaFailure) {
    return {
      priority,
      card: {
        projectId: project.projectId,
        clientName,
        title: "Review generation issue",
        why: "Latest run did not pass quality gate — open the project room to inspect.",
        cta: "Open project",
        actionId: "open-project",
      },
    };
  }
  if (hasPreview) {
    return {
      priority,
      card: {
        projectId: project.projectId,
        clientName,
        title: "Share preview link",
        why: "Preview is ready for client review.",
        cta: "Open project",
        actionId: "open-project",
      },
    };
  }
  if (generating) {
    return {
      priority,
      card: {
        projectId: project.projectId,
        clientName,
        title: "Generating preview",
        why: "Site generation is running — check progress in the project room.",
        cta: "Open project",
        actionId: "open-project",
      },
    };
  }
  return {
    priority,
    card: {
      projectId: project.projectId,
      clientName,
      title: "Continue project",
      why: awaitingClient ? "Waiting for client intake or approval." : "Pick up where you left off.",
      cta: "Open project",
      actionId: "open-project",
    },
  };
}

/**
 * Portfolio-level next actions for Command Center — one card per project, sorted by urgency.
 */
export function deriveGuidedNextActions(input: {
  readonly projects: readonly GuidedPortfolioProjectInput[];
  readonly reviews: Readonly<Record<string, ClientReviewStatusV1 | null>>;
  readonly departureKits?: Readonly<Record<string, StudioDepartureKitStatusV1 | null>>;
  readonly limit?: number | null;
}): readonly GuidedNextActionCard[] {
  const ranked = input.projects.map((project) =>
    buildPortfolioNextActionCard({
      project,
      review: input.reviews[project.projectId],
      departureKit: input.departureKits?.[project.projectId],
    }),
  );
  ranked.sort((a, b) => a.priority - b.priority || a.card.clientName.localeCompare(b.card.clientName));
  const cap = input.limit === undefined ? ranked.length : input.limit;
  const sliceEnd = cap == null ? ranked.length : cap;
  return ranked.slice(0, sliceEnd).map((row) => row.card);
}

export function deriveCommandCenterStats(input: {
  readonly missionSummary: StudioMissionControlSummaryV1 | null;
  readonly scanOpportunityCount: number;
}): readonly CommandCenterStatCard[] {
  const summary = input.missionSummary;
  return [
    {
      id: "waiting-clients",
      label: "Waiting on clients",
      count: summary?.clientsAwaitingApprovalCount ?? 0,
      hint: "Intake or approval pending in Client Portal.",
      navigateTo: "projects",
    },
    {
      id: "blocked-projects",
      label: "Blocked projects",
      count: summary?.blockedProjectCount ?? 0,
      hint: "Projects need agency attention before they can proceed.",
      navigateTo: "projects",
    },
    {
      id: "qa-failures",
      label: "QA failures",
      count: summary?.qaFailureCount ?? 0,
      hint: "Latest run did not pass quality gate.",
      navigateTo: "projects",
    },
    {
      id: "handoff-ready",
      label: "Handoff ready",
      count: summary?.handoffReadyCount ?? 0,
      hint: "Departure Kit can be packaged or downloaded.",
      navigateTo: "projects",
    },
    {
      id: "scan-opportunities",
      label: "Scan opportunities",
      count: input.scanOpportunityCount,
      hint: "Claimed public scans ready for outreach or relaunch.",
      navigateTo: "scan-opportunities",
    },
  ];
}

export function humanIntakeStatus(summary: StudioIntakeSummaryV1 | null): string {
  if (!summary) return "No intake started yet";
  if (summary.status === "SUBMITTED") return "Client finished the form";
  if (summary.linkStatus === "INTAKE_OPEN" || summary.linkStatus === "INTAKE_IN_PROGRESS") {
    return "Waiting for client";
  }
  return "Intake in progress";
}

export function humanRunStatusLabel(status: string | null | undefined): string {
  if (!status) return "Project started";
  if (isActiveGenerationRunStatus(status)) {
    return "Generating site…";
  }
  switch (status) {
    case "EXPORTED":
    case "QA_PASSED":
    case "QA_PASSED_WITH_WARNINGS":
      return "Site ready";
    case "FAILED":
    case "QA_FAILED":
    case "CANCELLED":
      return "Generation needs attention";
    default:
      return "Project started";
  }
}
